import {
  getPaceTypeLabel,
  parseBoolean,
  parseRankMetricValue,
  ParticipationListColumnPropValue,
  ParticipationListColumnType,
  RankMetric,
  toEuro,
  transformUrlsToLinks
} from "@spoferan/spoferan-ts-core";
import { useI18n, useNuxtApp } from "#imports";
export function useParticipationColumnDataDisplays(options = {}) {
  const { t } = useI18n();
  const { $date } = useNuxtApp();
  function parseTableHeaderDataForColumn(column) {
    const headerData = {
      text: column.label,
      abbreviation: column.abbreviation,
      value: column.value,
      help: column.is_protected ? t("label.protected") : null,
      default: "-",
      sort_value: column.sort_value ? column.sort_value : column.value,
      sortable: !!column.sort_value,
      width_factor: column.width_factor ?? 3,
      nowrap: true
      // The following is commented out because it is just for documentation (types) purposes
      // formatter: ({item, parent}) => {}, // A function to format the value of the cell for display within a table
      // flatFormatter: ({item, parent}) => {}, // A function to format the value of the cell into a plain string value
    };
    if (column.type === ParticipationListColumnType.Input) {
      if (column.input_type.is_boolean) {
        headerData.type = "bool";
        headerData.formatter = ({ item }) => {
          const inputData = item.inputs && item.inputs.length ? item.inputs.find((input) => input.id === column.input_id) : null;
          return inputData ? parseBoolean(inputData.value) : false;
        };
        headerData.flatFormatter = ({ item }) => {
          return headerData.formatter({ item }) ? t("label.yes") : t("label.no");
        };
      } else {
        headerData.min_width = 85;
        headerData.formatter = ({ item }) => {
          const inputData = item.inputs && item.inputs.length ? item.inputs.find((input) => input.id === column.input_id) : null;
          return inputData && inputData.value ? inputData.value : "-";
        };
        headerData.flatFormatter = headerData.formatter;
      }
      return headerData;
    }
    if (column.type === ParticipationListColumnType.Product) {
      headerData.type = "bool";
      headerData.formatter = ({ item }) => {
        if (item.participations && options.selectedList?.value?.group_prop) {
          return item.participations.every((participation) => {
            const inputData2 = participation.products && participation.products.length ? participation.products.find((product) => product.id === column.product_id) : null;
            return inputData2 && inputData2.exists;
          });
        }
        const inputData = item.products && item.products.length ? item.products.find((product) => product.id === column.product_id) : null;
        return inputData && inputData.exists;
      };
      headerData.flatFormatter = ({ item }) => {
        return headerData.formatter({ item }) ? t("label.yes") : t("label.no");
      };
      return headerData;
    }
    if (column.type === ParticipationListColumnType.ProductInput) {
      if (column.input_type.is_boolean) {
        headerData.type = "bool";
        headerData.formatter = ({ item }) => {
          const inputData = item.productInputs && item.productInputs.length ? item.productInputs.find((input) => input.id === column.product_input_id) : null;
          return inputData ? parseBoolean(inputData.value) : false;
        };
        headerData.flatFormatter = ({ item }) => {
          return headerData.formatter({ item }) ? t("label.yes") : t("label.no");
        };
      } else {
        headerData.min_width = 85;
        headerData.formatter = ({ item }) => {
          const inputData = item.productInputs && item.productInputs.length ? item.productInputs.find((input) => input.id === column.product_input_id) : null;
          return inputData && inputData.value ? inputData.value : "-";
        };
        headerData.flatFormatter = headerData.formatter;
      }
      return headerData;
    }
    if (column.type === ParticipationListColumnType.Rank) {
      headerData.width = 50;
      headerData.formatter = ({ item }) => {
        const rankData = item.ranks && item.ranks.length ? item.ranks.find((rank) => `rank.${rank.id}` === column.value) : null;
        return rankData && rankData.value ? `${rankData.value}.` : "-";
      };
      headerData.flatFormatter = headerData.formatter;
      return headerData;
    }
    if (column.type === ParticipationListColumnType.Result) {
      headerData.formatter = ({ item }) => {
        const resultData = item.results && item.results.length ? item.results.find((result) => result.id === column.track_spot_id) : null;
        return resultData && resultData.value ? parseRankMetricValue(RankMetric.Time, resultData.value) : "-";
      };
      headerData.flatFormatter = headerData.formatter;
      return headerData;
    }
    if (column.type === ParticipationListColumnType.Order || column.type === ParticipationListColumnType.Action) {
      headerData.value = headerData.value.split(".")[1];
      return headerData;
    }
    switch (column.value) {
      case ParticipationListColumnPropValue.AVATAR:
        headerData.abbreviation = " ";
        headerData.width = 24;
        break;
      case ParticipationListColumnPropValue.NAME:
        headerData.min_width = 120;
        headerData.flatFormatter = ({ item }) => `${item.last_name}, ${item.first_name}`;
        break;
      case ParticipationListColumnPropValue.USER:
        headerData.min_width = 120;
        headerData.flatFormatter = ({ item }) => {
          return item.display_name;
        };
        break;
      case ParticipationListColumnPropValue.RANK:
        headerData.width = 50;
        headerData.formatter = ({ item, parent }) => {
          if (parent) {
            return "";
          }
          return item.rank ? `${item.rank}.` : "-";
        };
        headerData.flatFormatter = headerData.formatter;
        break;
      case ParticipationListColumnPropValue.PRIMARY_RANK_VALUE:
        headerData.text = options.resultListGroup && options.resultListGroup.primary_rank_value_prop ? t(`enum.rank_metric.${options.resultListGroup.primary_rank_value_prop}.abbreviation`) : headerData.text;
        break;
      case ParticipationListColumnPropValue.SECONDARY_RANK_VALUE:
        headerData.text = options.resultListGroup && options.resultListGroup.secondary_rank_value_prop ? t(`enum.rank_metric.${options.resultListGroup.secondary_rank_value_prop}.abbreviation`) : headerData.text;
        break;
      case ParticipationListColumnPropValue.PARTICIPATION_CLASS:
        headerData.formatter = ({ item }) => {
          return item.participationClass ? item.participationClass.name : "-";
        };
        headerData.flatFormatter = headerData.formatter;
        break;
      case ParticipationListColumnPropValue.PARTICIPANT_ID:
        headerData.align = "center";
        headerData.flatFormatter = ({ item }) => {
          return item.participant_id;
        };
        break;
      case ParticipationListColumnPropValue.ROWS_COUNT:
        headerData.align = "center";
        break;
      case ParticipationListColumnPropValue.AGE_CLASS:
        headerData.min_width = 48;
        headerData.flatFormatter = ({ item }) => {
          return item.ageClass?.name ?? "-";
        };
        break;
      case ParticipationListColumnPropValue.BIRTHDAY:
        headerData.type = "date";
        headerData.flatFormatter = ({ item }) => {
          return $date(item.birthday).format("L");
        };
        break;
      case ParticipationListColumnPropValue.GENDER:
        headerData.formatter = ({ item, reduced }) => {
          if (!item.gender) {
            return "-";
          }
          if (reduced) {
            return item.gender === "f" ? t("abbreviation.female") : t("abbreviation.male");
          }
          return item.gender === "f" ? t("label.female") : t("label.male");
        };
        headerData.flatFormatter = headerData.formatter;
        break;
      case ParticipationListColumnPropValue.STATE:
        headerData.formatter = ({ item }) => {
          return item.state ? item.state.locale_name : "-";
        };
        headerData.flatFormatter = ({ item }) => item.state?.locale_name ?? "-";
        break;
      case ParticipationListColumnPropValue.COUNTRY:
        headerData.flatFormatter = ({ item }) => item.country?.locale_name ?? "-";
        break;
      case ParticipationListColumnPropValue.NATIONALITY:
        headerData.flatFormatter = ({ item }) => item.country?.ioc_code ?? "-";
        break;
      case ParticipationListColumnPropValue.TIME:
        headerData.formatter = ({ item }) => parseRankMetricValue(RankMetric.Time, item.time);
        headerData.flatFormatter = headerData.formatter;
        break;
      case ParticipationListColumnPropValue.ROUNDS:
        headerData.formatter = ({ item }) => parseRankMetricValue(RankMetric.Rounds, item.rounds);
        headerData.flatFormatter = headerData.formatter;
        break;
      case ParticipationListColumnPropValue.POINTS:
        headerData.formatter = ({ item }) => parseRankMetricValue(RankMetric.Points, item.points);
        headerData.flatFormatter = headerData.formatter;
        break;
      case ParticipationListColumnPropValue.DISTANCE:
        headerData.formatter = ({ item }) => parseRankMetricValue(RankMetric.Distance, item.distance);
        headerData.flatFormatter = headerData.formatter;
        break;
      case ParticipationListColumnPropValue.ALTITUDE:
        headerData.formatter = ({ item }) => parseRankMetricValue(RankMetric.Altitude, item.altitude);
        headerData.flatFormatter = headerData.formatter;
        break;
      case ParticipationListColumnPropValue.RECEIVE_NEWS:
      case ParticipationListColumnPropValue.RECEIVE_UPDATES:
        headerData.type = "bool";
        headerData.min_width = 78;
        headerData.flatFormatter = ({ item }) => {
          return parseBoolean(item[column.value] ?? false) ? t("label.yes") : t("label.no");
        };
        break;
      case ParticipationListColumnPropValue.STARTED_AT:
      case ParticipationListColumnPropValue.FINISHED_AT:
      case ParticipationListColumnPropValue.DELETED_AT:
        headerData.type = "datetime";
        headerData.flatFormatter = ({ item }) => {
          return $date(item[column.value]).format("LLL");
        };
        break;
      case ParticipationListColumnPropValue.CREATED_AT:
      case ParticipationListColumnPropValue.PARTICIPATED_AT:
        headerData.type = "date";
        headerData.flatFormatter = ({ item }) => {
          return $date(item[column.value]).format("L");
        };
        break;
      case ParticipationListColumnPropValue.PAYMENT_AMOUNT:
        headerData.type = "amount";
        headerData.flatFormatter = ({ item }) => {
          return toEuro(item.payment_amount);
        };
        break;
      case ParticipationListColumnPropValue.PACE:
        headerData.formatter = ({ item }) => {
          if (!item.pace || !options.paceType) {
            return "-";
          }
          return `${item.pace} ${getPaceTypeLabel(options.paceType.value)}`;
        };
        headerData.flatFormatter = headerData.formatter;
        break;
      case ParticipationListColumnPropValue.RESULT_INFO:
      case ParticipationListColumnPropValue.REGISTER_INFO:
      case ParticipationListColumnPropValue.PARTICIPANT_INFO:
        headerData.nowrap = false;
        headerData.type = "html";
        headerData.min_width = 220;
        headerData.formatter = ({ item }) => item[column.value] ? transformUrlsToLinks(item[column.value]) : "-";
        headerData.flatFormatter = ({ item }) => item[column.value] ? item[column.value] : "-";
        break;
      case ParticipationListColumnPropValue.CLUB:
        headerData.flatFormatter = ({ item }) => item.club?.name ?? "-";
        break;
      case ParticipationListColumnPropValue.STATUS:
        headerData.flatFormatter = ({ item }) => item.status?.title ?? "-";
        break;
      case ParticipationListColumnPropValue.SUB_STATUS:
        headerData.flatFormatter = ({ item }) => item.sub_status?.title ?? "-";
        break;
      case ParticipationListColumnPropValue.PAYMENT_STATUS:
        headerData.flatFormatter = ({ item }) => item.payment_status?.title ?? "-";
        break;
      case ParticipationListColumnPropValue.PAYMENT_TYPE:
        headerData.flatFormatter = ({ item }) => item.payment_type ?? "-";
        break;
      case ParticipationListColumnPropValue.PRIVATE:
        headerData.flatFormatter = ({ item }) => {
          if (item.private === true) {
            return t("label.yes");
          } else if (item.private === false) {
            return t("label.no");
          }
          return "-";
        };
        break;
      case ParticipationListColumnPropValue.COUPON:
        headerData.flatFormatter = ({ item }) => item.coupon?.code ?? "-";
        break;
    }
    return headerData;
  }
  return {
    parseTableHeaderDataForColumn
  };
}
